/**
 * [mojdzial1006, paczka MD-KIT, projekt §2.2–2.3, §2.12–2.15, D-1, D-7] PIĄTY REJESTR — WSPÓLNE KAWAŁKI MODUŁU „MÓJ DZIAŁ”.
 *
 * Polecenie właściciela z 6.10.2026: „przygotuj mi całe nowe UI dla /moj-dzial/ (…) w każdej możliwej podstronie użyteczności
 * tego modułu, całe UI do zmiany”, a 7.10: „zrób wszystko”. Mój Dział dostaje TĘ SAMĄ powłokę co cztery strony panelu
 * (rozstrzygnięcie projektu, rozdz. 0): pięć grup, 18 miejsc, każde pod dzisiejszym adresem.
 *
 * Ten plik trzyma to, czego potrzebuje więcej niż jedna grupa rejestru (`start.js`, `wnioski.js`, `czas.js`, `ludzie.js`,
 * `zespol.js`, `praca.js` — pliki grup mają różnych właścicieli w paczkach obszarów, rozdz. 6):
 *   • budowę miejsca Z ZAKŁADKI `MY_DEPT_TABS` (`miejsceZZakladki`): `id` = dzisiejszy `id` zakładki = segment PL, segment
 *     EN = ostatni segment EN z `ROUTE_MAP`, `kluczTrasy` = dzisiejszy `route`, `klucz` = dzisiejsze `permission`, ikona
 *     zakładki. ŻADEN adres się nie zmienia i ŻADEN `id` nie dochodzi — lustro serwera (`ZakresMojegoDzialu`) i panel wejść
 *     imiennych zostają nietknięte. Etykiety i grupy z projektu (2.2) stoją TU, a nie w `MY_DEPT_TABS` — tamte zmienia dopiero
 *     MD-WLACZ (do tego czasu stary hub i menu boczne mają stare etykiety, ZM-30);
 *   • katalog OBIEKTÓW modułu (2.3): karty, szuflady, podgląd, okno, obiekt w treści — z właścicielem (paczką) i plikiem;
 *     każde miejsce zna obiekty karty osoby (karta osoby otwiera się z KAŻDEGO miejsca jednym kliknięciem, 2.10);
 *   • słowa: grupy, słownik pojęć (2.15), „gdzie co było”, stare nazwy miejsc, zwroty zakazane modułu;
 *   • `stareAdresy` — czysta funkcja tabeli 2.13 (zapadka MD-10);
 *   • zakładki osób operacyjnych — lustro `ZakladkiMojDzial.ZakladkiOsobOperacyjnych` serwera (2.6).
 *
 * Słowo w kodzie a słowo na ekranie: w kodzie „obszar”, NA EKRANIE „miejsce” (N-17).
 */
import { MY_DEPT_TABS } from "../../myDeptTabs";
import { ROUTE_MAP } from "../../../constants/routes";
import { GRUPA_STARTU, grupaStartu, obszar } from "../ksztalt";

/** Slug strony i baza adresu (te same co dziś: `/moj-dzial/*`, `/my-dept/*`). */
export const STRONA_MOJEGO_DZIALU = "moj-dzial";
export const TYTUL_MOJEGO_DZIALU = "Mój Dział";
export const ZDANIE_MOJEGO_DZIALU = "Ludzie, za których odpowiadasz: co czeka na Ciebie i kto dziś jest w pracy.";
export const BAZA_MOJEGO_DZIALU = Object.freeze({ pl: "/moj-dzial", en: "/my-dept" });
/* Trasy deweloperskie nowego modułu do MD-WLACZ (D21: produkcja nie widzi zmian) stoją WYŁĄCZNIE w App.js pod warunkiem
   `NODE_ENV === "development"` — żadna stała z ich adresem nie trafia do rejestru (rejestr jest w paczce głównej; MD-16). */

/** Etykieta miejsca startowego — w Moim Dziale „Start” (2.2, 2.15: „Start” = miejsce, „Do załatwienia” = grupa i lista). */
export const ETYKIETA_STARTU_MOJEGO_DZIALU = "Start";

/** Identyfikatory pięciu grup (D3). Druga grupa to „Wnioski” — wyjątek od „druga = Praca” (`drugaGrupa`, N-17). */
export const GRUPY_MOJEGO_DZIALU = Object.freeze({
    start: GRUPA_STARTU,
    wnioski: "wnioski",
    czas: "czas-pracy",
    zespol: "zespol",
    praca: "praca-i-rozwoj",
});

/** Grupy w kolejności paska (etykieta grupy ≠ etykieta żadnego miejsca — Z-12). */
export const grupyMojegoDzialu = () => [
    grupaStartu(),
    { id: GRUPY_MOJEGO_DZIALU.wnioski, etykieta: "Wnioski" },
    { id: GRUPY_MOJEGO_DZIALU.czas, etykieta: "Czas pracy" },
    { id: GRUPY_MOJEGO_DZIALU.zespol, etykieta: "Zespół" },
    { id: GRUPY_MOJEGO_DZIALU.praca, etykieta: "Praca i rozwój" },
];

/** Parametr filtra trwałego (ZM-20) — przenoszony przy KAŻDYM przejściu; nigdy obiekt (N-18). */
export const PARAMETR_DZIALU = "dzial";

// ── Obiekty modułu (2.3, 2.8 `WarstwaObiektow`) ────────────────────────────────────────────────

/**
 * KATALOG OBIEKTÓW. `rodzaj` z `RODZAJE_OBIEKTOW` (karta, szuflada, podgląd, okno — wyjątek Z-15 `KanbanTaskModal`,
 * treść — obiekt rysowany w miejscu treści, D-8). `paczka` = właściciel pliku komponentu (rozdz. 6), `plik` = ścieżka względem
 * `pages/mojDzial/` (zaślepka „W budowie” z MD-KIT, którą wypełnia paczka właściciela). `warstwa`: „karta” (najwyżej jedna;
 * otwarta z karty ZAMIENIA ją) albo „nad” (szuflada, podgląd, okno — najwyżej jedna, wolno nad kartą). `rysujeMiejsce` = obiekt,
 * który rysuje sam komponent miejsca (karta procedury paczki PROC) — `WarstwaObiektow` go pomija.
 */
export const OBIEKTY_MOJEGO_DZIALU = Object.freeze({
    osoba: {
        parametr: "osoba", rodzaj: "karta", warstwa: "karta", nazwa: "karta osoby", dopelniacz: "karty osoby",
        czesci: ["dzis", "urlopy", "wnioski", "zdalna", "praca", "rozwoj", "sprzet", "kontakt"],
        pokaz: { rozwoj: ["notatki", "szkolenia", "sciezka"] },
        paczka: "MD-START", plik: "obiekty/KartaOsoby/KartaOsoby",
    },
    wniosek: {
        parametr: "wniosek", rodzaj: "karta", warstwa: "karta", nazwa: "wniosek", dopelniacz: "wniosku",
        czesci: ["podsumowanie", "decyzja", "tresc", "sciezka", "komentarze", "dokumenty", "kadry"],
        paczka: "MD-WNIOSKI", plik: "obiekty/KartaWniosku/KartaWniosku",
    },
    zasob: {
        parametr: "zasob", rodzaj: "karta", warstwa: "karta", nazwa: "zasób działu", dopelniacz: "zasobu działu",
        czesci: ["podsumowanie", "obsada", "nadaj", "prosby", "akceptanci", "historia"],
        paczka: "MD-ZESPOL", plik: "obiekty/KartaZasobu",
    },
    wydanie: {
        parametr: "wydanie", rodzaj: "karta", warstwa: "karta", nazwa: "wydany dokument", dopelniacz: "wydanego dokumentu",
        czesci: ["podsumowanie", "decyzja", "wysylka", "podpis", "skany", "audyt", "grozne"],
        paczka: "MD-ZESPOL", plik: "obiekty/KartaWydania/KartaWydania",
    },
    cel: {
        parametr: "cel", rodzaj: "karta", warstwa: "karta", nazwa: "cel", dopelniacz: "celu",
        czesci: ["podsumowanie", "decyzja", "dane", "postep", "wartosc", "ocena", "powiazania", "os"],
        paczka: "MD-PRACA", plik: "obiekty/KartaCelu/KartaCelu",
    },
    sciezka: {
        parametr: "sciezka", rodzaj: "karta", warstwa: "karta", nazwa: "ścieżka kariery", dopelniacz: "ścieżki kariery",
        czesci: ["plan", "os", "kompetencje", "szkolenia", "dokumenty", "osiagniecia"],
        paczka: "MD-PRACA", plik: "obiekty/KartaSciezki",
    },
    doc: {
        parametr: "doc", rodzaj: "karta", warstwa: "karta", nazwa: "procedura", dopelniacz: "procedury",
        dodatkowe: ["etap"], rysujeMiejsce: true, paczka: "PROC (aneks PROC-MD)", plik: null,
    },
    dzien: {
        parametr: "dzien", rodzaj: "szuflada", warstwa: "nad", nazwa: "dzień grafiku", dopelniacz: "dnia grafiku",
        paczka: "MD-CZAS", plik: "obiekty/SzufladaDnia",
    },
    zakres: {
        parametr: "zakres", rodzaj: "szuflada", warstwa: "nad", nazwa: "urlop z kalendarza", dopelniacz: "urlopu z kalendarza",
        paczka: "MD-CZAS", plik: "obiekty/SzufladaZakresu",
    },
    raport: {
        parametr: "raport", rodzaj: "szuflada", warstwa: "nad", nazwa: "raport dnia zdalnego", dopelniacz: "raportu dnia zdalnego",
        paczka: "MD-CZAS", plik: "obiekty/SzufladaRaportu",
    },
    zlecenie: {
        parametr: "zlecenie", rodzaj: "szuflada", warstwa: "nad", nazwa: "zlecenie z kadr", dopelniacz: "zlecenia z kadr",
        paczka: "MD-WNIOSKI", plik: "obiekty/SzufladaZlecenia",
    },
    prosba: {
        parametr: "prosba", rodzaj: "szuflada", warstwa: "nad", nazwa: "prośba o dostęp", dopelniacz: "prośby o dostęp",
        paczka: "MD-ZESPOL", plik: "obiekty/SzufladaProsby",
    },
    wpis: {
        parametr: "wpis", rodzaj: "szuflada", warstwa: "nad", nazwa: "hasło w sejfie", dopelniacz: "hasła w sejfie",
        paczka: "MD-ZESPOL", plik: "obiekty/SzufladaWpisuSejfu",
    },
    zadanie: {
        parametr: "zadanie", rodzaj: "szuflada", warstwa: "nad", nazwa: "zadanie", dopelniacz: "zadania",
        paczka: "MD-PRACA", plik: "obiekty/SzufladaZadania",
    },
    notatka: {
        parametr: "notatka", rodzaj: "szuflada", warstwa: "nad", nazwa: "notatka o pracy", dopelniacz: "notatki o pracy",
        paczka: "MD-PRACA", plik: "obiekty/SzufladaNotatki",
    },
    kurs: {
        parametr: "kurs", rodzaj: "szuflada", warstwa: "nad", nazwa: "kurs", dopelniacz: "kursu",
        paczka: "MD-PRACA", plik: "obiekty/SzufladaKursu",
    },
    protokol: {
        parametr: "protokol", rodzaj: "podglad", warstwa: "nad", nazwa: "protokół sprzętu", dopelniacz: "protokołu sprzętu",
        paczka: "MD-ZESPOL", plik: "obiekty/PodgladProtokolu",
    },
    zadanieTablicy: {
        parametr: "zadanieTablicy", rodzaj: "okno", warstwa: "nad", nazwa: "zadanie tablicy", dopelniacz: "zadania tablicy",
        paczka: "MD-PRACA", plik: "obiekty/OknoZadaniaTablicy",
    },
    tablica: {
        parametr: "tablica", rodzaj: "tresc", warstwa: "tresc", nazwa: "tablica", dopelniacz: "tablicy",
        paczka: "MD-PRACA", plik: "obiekty/TablicaWTresci",
    },
});

/** Obiekty dostępne z karty osoby — karta otwiera się w KAŻDYM miejscu, więc każde miejsce je zna (2.10, 2.11, 3.0). */
export const OBIEKTY_KARTY_OSOBY = Object.freeze(["osoba", "dzien", "notatka", "raport", "protokol", "wniosek", "cel", "sciezka", "zasob"]);

/** Kolejność w miejscu: najpierw warstwy „nad” i treść, potem karty — `otwartyObiekt` oddaje wierzchnią warstwę. */
const KOLEJNOSC_WARSTW = { nad: 0, tresc: 1, karta: 2 };

/**
 * Obiekty miejsca: jego własne ∪ obiekty karty osoby (te same OBIEKTY definicji — „jedna nazwa, jedno znaczenie”, N-18).
 * @param {...string} wlasne parametry obiektów miejsca
 */
export const obiektyMiejsca = (...wlasne) => {
    const parametry = [...new Set([...wlasne, ...OBIEKTY_KARTY_OSOBY])];
    return parametry
        .map((p, i) => ({ p, i }))
        .sort((a, b) => (KOLEJNOSC_WARSTW[OBIEKTY_MOJEGO_DZIALU[a.p].warstwa] - KOLEJNOSC_WARSTW[OBIEKTY_MOJEGO_DZIALU[b.p].warstwa]) || (a.i - b.i))
        .map(({ p }) => OBIEKTY_MOJEGO_DZIALU[p]);
};

// ── Kreatory modułu (2.16, rozdz. 3) ───────────────────────────────────────────────────────────

/**
 * KATALOG KREATORÓW: `kroki` RAZEM z ostatnim „Sprawdź” (lustro `Kreator`), `jedenEkran` (Z-18 — tylko „Sprawdź”),
 * `rodzaj: "formularz"` (jedna `SzufladaFormularza` otwierana adresem, D-8), `zewnetrzny` (cudzy kreator sterowany
 * `krok`/`onKrok` — `ManagerRequestDrawer`, `HrNewRequestDrawer`, kreator paczki PROC; bez wymogu „Sprawdź”, zapis pyta 1b).
 */
export const KREATORY_MOJEGO_DZIALU = Object.freeze({
    "wniosek-w-imieniu": { nazwa: "wniosek-w-imieniu", tytul: "Wniosek w imieniu pracownika", kroki: 3, zewnetrzny: true, paczka: "MD-WNIOSKI", plik: "kreatory/WniosekWImieniu" },
    "wniosek-wlasny": { nazwa: "wniosek-wlasny", tytul: "Mój wniosek", kroki: 4, zewnetrzny: true, paczka: "MD-WNIOSKI", plik: "kreatory/WniosekWlasny" },
    "uloz-grafik": { nazwa: "uloz-grafik", tytul: "Ułóż grafik", kroki: 4, paczka: "MD-CZAS", plik: "kreatory/UlozGrafik" },
    "zatwierdz-tydzien": { nazwa: "zatwierdz-tydzien", tytul: "Zatwierdź tydzień", kroki: 1, jedenEkran: true, paczka: "MD-CZAS", plik: "kreatory/ZatwierdzTydzien" },
    "opublikuj-szkice": { nazwa: "opublikuj-szkice", tytul: "Opublikuj szkice", kroki: 1, jedenEkran: true, paczka: "MD-CZAS", plik: "kreatory/OpublikujSzkice" },
    "udziel-urlopu": { nazwa: "udziel-urlopu", tytul: "Udziel urlopu", kroki: 2, paczka: "MD-CZAS", plik: "kreatory/UdzielUrlopu" },
    "potwierdz-pomieszczenia": { nazwa: "potwierdz-pomieszczenia", tytul: "Potwierdź pomieszczenia", kroki: 2, paczka: "MD-ZESPOL", plik: "kreatory/PotwierdzPomieszczenia" },
    "zmien-pomieszczenie": { nazwa: "zmien-pomieszczenie", tytul: "Zmień pomieszczenie", kroki: 1, rodzaj: "formularz", paczka: "MD-ZESPOL", plik: "kreatory/ZmienPomieszczenie" },
    "dodaj-haslo": { nazwa: "dodaj-haslo", tytul: "Dodaj hasło", kroki: 1, rodzaj: "formularz", paczka: "MD-ZESPOL", plik: "kreatory/DodajHaslo" },
    "wydaj-dokument": { nazwa: "wydaj-dokument", tytul: "Wydaj dokument", kroki: 4, paczka: "MD-ZESPOL", plik: "kreatory/WydajDokument" },
    "przypisz-ludziom": { nazwa: "przypisz-ludziom", tytul: "Przypisz moim ludziom", kroki: 3, zewnetrzny: true, rysujeMiejsce: true, paczka: "PROC (aneks PROC-MD)", plik: null },
    "dodaj-zadanie": { nazwa: "dodaj-zadanie", tytul: "Dodaj zadanie", kroki: 1, rodzaj: "formularz", paczka: "MD-PRACA", plik: "kreatory/DodajZadanie" },
    "nowa-tablica": { nazwa: "nowa-tablica", tytul: "Nowa tablica", kroki: 2, paczka: "MD-PRACA", plik: "kreatory/NowaTablica" },
    "przypisz-cel": { nazwa: "przypisz-cel", tytul: "Przypisz cel", kroki: 4, paczka: "MD-PRACA", plik: "kreatory/PrzypiszCel" },
    "cel-dzialu": { nazwa: "cel-dzialu", tytul: "Cel działu", kroki: 4, paczka: "MD-PRACA", plik: "kreatory/CelDzialu" },
    "wniosek-o-ocene": { nazwa: "wniosek-o-ocene", tytul: "Wniosek o ocenę wdrożeniową", kroki: 1, jedenEkran: true, zewnetrzny: true, paczka: "MD-PRACA", plik: "kreatory/WniosekOOcene" },
    "dodaj-notatke": { nazwa: "dodaj-notatke", tytul: "Dodaj notatkę", kroki: 1, rodzaj: "formularz", paczka: "MD-PRACA", plik: "kreatory/DodajNotatke" },
});

/** Kreatory miejsca z katalogu (w kolejności podanej). */
export const kreatoryMiejsca = (...nazwy) => nazwy.map((n) => KREATORY_MOJEGO_DZIALU[n]);

// ── Miejsce z zakładki MY_DEPT_TABS (D-1) ──────────────────────────────────────────────────────

/** Ostatni segment adresu z mapy tras (`/my-dept/worktime` → `worktime`). */
const ostatniSegment = (adres) => String(adres || "").split("/").filter(Boolean).pop() || "";

/**
 * MIEJSCE Z ZAKŁADKI: wszystko, co dziś stoi w `MY_DEPT_TABS` i `ROUTE_MAP`, plus to, czym miejsce wyróżnia się w projekcie.
 * Nieznany `id` zakładki = błąd budowy rejestru (rzuca od razu — rejestr powstaje przy imporcie modułu).
 * @param {string} idZakladki dzisiejszy `id` zakładki (= segment PL)
 * @param {object} dane etykieta, zdanie, grupa, wzór, widoki, filtry, obiekty, kreatory, pasek, głowa, sekcje, słowa…
 */
export const miejsceZZakladki = (idZakladki, dane) => {
    const zakladka = MY_DEPT_TABS.find((t) => t.id === idZakladki);
    if (!zakladka) throw new Error(`Rejestr Mojego Działu: nie ma zakładki „${idZakladki}” w MY_DEPT_TABS.`);
    const trasa = ROUTE_MAP[zakladka.route];
    if (!trasa) throw new Error(`Rejestr Mojego Działu: zakładka „${idZakladki}” wskazuje klucz trasy „${zakladka.route}”, którego nie ma w ROUTE_MAP.`);
    const { filtry = [], ...reszta } = dane;
    return obszar({
        id: zakladka.id,
        segment: { pl: zakladka.id, en: ostatniSegment(trasa.en) },
        kluczTrasy: zakladka.route,
        ikona: zakladka.icon,
        ...(zakladka.permission ? { klucz: zakladka.permission } : {}),
        // Zakładka serwera tego miejsca (tryby i wejścia imienne, ZM-29) — w miejscach równa `id`.
        zakladka: zakladka.id,
        zdarzeniaNaZywo: [],
        sekcje: [],
        ...reszta,
        // Dział jest filtrem TRWAŁYM każdego miejsca (ZM-20) — zostaje po zamknięciu obiektu i przy każdym przejściu.
        filtry: [PARAMETR_DZIALU, ...filtry.filter((f) => f !== PARAMETR_DZIALU)],
    });
};

// ── Zakładki serwera (2.5, 2.6) ────────────────────────────────────────────────────────────────

/**
 * LUSTRO `ZakladkiMojDzial.ZakladkiOsobOperacyjnych` (src/DogSoft.Application/Permissions/ZakresMojegoDzialu.cs):
 * zakładki, pod którymi serwer pokazuje osoby z zasięgu OPERACYJNEGO — tylko z nimi `/my-dept/members` zawęża skład.
 * Zgodność z serwerem pilnuje `pages/mojDzial/__tests__/zapadki/lustroZakladekOsob.mojDzial.test.js` (czyta plik C#:
 * `Wszystkie` minus `ZakladkiOsobWidocznych`, w tej samej kolejności). „pulpit” stoi na liście serwera, ale moduł go
 * NIE wybiera jako domyślnej zakładki składu (koniec 403 u wejścia imiennego bez „pulpit”, 2.6).
 */
export const ZAKLADKI_OSOB_OPERACYJNYCH = Object.freeze([
    "pulpit", "wnioski-przelozonego", "grafik", "urlopy", "praca-zdalna", "czas-pracy", "wyposazenie", "sejf",
    "uprawnienia", "ocena-pracownika", "e-learning", "cele", "todo", "kanban",
]);

// ── Słowa (2.15) ───────────────────────────────────────────────────────────────────────────────

/** Słownik pojęć modułu — każde jedno zdanie ≤ 160 znaków (N-7); całość w „Więcej ▾ → Pojęcia”. */
export const SLOWNIK_POJEC_MOJEGO_DZIALU = Object.freeze([
    { slowo: "zasięg", definicja: "Ludzie, za których odpowiadasz z funkcji w strukturze — nie wszyscy z działu, do którego należysz." },
    { slowo: "dział raportujący", definicja: "Dział, którego kierownik raportuje do Ciebie: widzisz jego wnioski, ludzi, dokumenty i procedury, resztę prowadzi kierownik." },
    { slowo: "przełożony dyrektora", definicja: "Osoba, której raportuje dyrektor działu: widzi wnioski, ludzi, dokumenty, procedury, cele i swoje sprawy, a działem kieruje dyrektor." },
    { slowo: "wejście imienne", definicja: "Dostęp do działu nadany Ci imiennie przez administratora, tylko do miejsc wskazanych w panelu." },
    { slowo: "urlop z wniosku", definicja: "Urlop, o który pracownik poprosił wnioskiem; decyzja idzie ścieżką akceptacji do kadr." },
    { slowo: "urlop z kalendarza", definicja: "Urlop wpisany w kalendarz zespołu i zatwierdzany przez przełożonego bez wniosku do kadr." },
    { slowo: "saldo pozostałe", definicja: "Dni do wykorzystania po odjęciu zatwierdzonych i oczekujących urlopów." },
    { slowo: "zaległy urlop", definicja: "Urlop z poprzedniego roku: udziel do 30 września, inaczej po 3 latach przepada." },
    { slowo: "szkic grafiku", definicja: "Grafik zapisany, ale nieopublikowany — pracownik go nie widzi i nie dostał powiadomienia." },
    { slowo: "dzień zatwierdzony", definicja: "Pracownik nie zmieni go sam — zmienisz go Ty, zdejmując zatwierdzenie." },
    { slowo: "plan dnia", definicja: "Lista zadań jednego dnia pracownika, którą możesz mu wysłać e-mailem." },
    { slowo: "raport dnia zdalnego", definicja: "Opis dnia pracy zdalnej: godziny i zadania, który pracownik składa, a Ty zatwierdzasz albo zwracasz do korekty." },
    { slowo: "limit dni zdalnych", definicja: "Liczba dni pracy zdalnej, które pracownik może wykorzystać w roku według ustaleń z kadrami." },
    { slowo: "dzień bez raportu", definicja: "Dzień pracy zdalnej, za który pracownik nie złożył raportu w ciągu dwóch dni." },
    { slowo: "poziom przełożonego", definicja: "Widok aktywności bez treści i adresów: liczby dni, godzin i czynności, które wolno zobaczyć przełożonemu." },
    { slowo: "opiekun zasobu", definicja: "Osoba, która odpowiada za dysk albo zasób działu i może nadawać do niego dostęp." },
    { slowo: "akceptant", definicja: "Osoba, która rozstrzyga prośby o dostęp do zasobu działu." },
    { slowo: "odczyt / odczyt i zapis", definicja: "Odczyt pozwala tylko oglądać pliki, a odczyt i zapis pozwala je też zmieniać i dodawać." },
    { slowo: "klucze w aplikacji", definicja: "Uprawnienia osoby w mPracowniku, nadawane przez administratora w panelu dostępu." },
    { slowo: "bank haseł", definicja: "Hasła jednego działu w sejfie, dostępne dla osób, które do niego należą i nim kierują." },
    { slowo: "lista kontrolna", definicja: "Spis kroków do odhaczenia przy procedurze albo przy wydaniu sprzętu." },
    { slowo: "tryb próbny powiadomień", definicja: "System liczy terminy i pokazuje sprawy, ale nie wysyła powiadomień do ludzi." },
    { slowo: "ocena wdrożeniowa", definicja: "Ocena nowego pracownika pod koniec okresu wdrożenia, wystawiana wnioskiem do kadr (procedura INS-ZKL-02, Załącznik nr 3)." },
    { slowo: "skala oceny celu", definicja: "Słowna ocena realizacji celu po okresie, od „nieosiągnięty” do „przekroczony”." },
    { slowo: "cel rozwojowy", definicja: "Cel, który rozwija umiejętności pracownika, a nie mierzy wyniku jego pracy." },
    { slowo: "kamień milowy", definicja: "Etap celu z własnym terminem, który odhaczasz po osiągnięciu." },
    { slowo: "wartość oceniającego", definicja: "Waga celu nadana przez oceniającego; możesz ją raz zmniejszyć, najwyżej o połowę." },
    { slowo: "niska obsada", definicja: "Dzień, w którym w pracy jest mniej osób niż minimum ustalone dla zespołu." },
    { slowo: "jednostka", definicja: "Część działu w strukturze organizacyjnej, z własnym kierownikiem i ludźmi." },
    { slowo: "MPK", definicja: "Miejsce powstawania kosztów — kod, pod którym księgowość rozlicza koszty osoby." },
    { slowo: "dział główny", definicja: "Dział, do którego osoba należy przede wszystkim; pozostałe jej przypisania to działy dodatkowe." },
    { slowo: "dopasowane po nazwisku", definicja: "Sprzęt przypisany osobie po zgodności imienia i nazwiska, bo w ewidencji brakowało jej konta." },
]);

/** „Gdzie co było” (2.15) — stara nazwa → nowe miejsce. `bylo` NIE wchodzi do skanu słów (stara nazwa stoi celowo). */
export const GDZIE_CO_BYLO_MOJEGO_DZIALU = Object.freeze([
    { bylo: "Pulpit", jest: "Start", obszar: "start" },
    { bylo: "Kafle liczb na pulpicie", jest: "Lista „Do załatwienia” i kafle miejsc na starcie", obszar: "start" },
    { bylo: "Moje do wypełnienia", jest: "Wnioski → Moje sprawy", obszar: "wnioski-przelozonego" },
    { bylo: "Zespół w strukturze", jest: "Zespół → Ludzie", obszar: "struktura" },
    { bylo: "Struktura pode mną", jest: "Ludzie → Działy i jednostki", obszar: "struktura" },
    { bylo: "Kalendarz — w tym miesiącu", jest: "Ludzie → W tym miesiącu", obszar: "struktura" },
    { bylo: "Moje zespoły Teams", jest: "Ludzie → Zespoły Teams", obszar: "struktura" },
    { bylo: "Uprawnienia: Co zespół ma, Co mogę nadać, Zasoby działu, Prośby", jest: "Dostępy: żetony listy i sekcja „Dostępy osób”", obszar: "uprawnienia" },
    { bylo: "Wyposażenie IT", jest: "Zespół → Sprzęt", obszar: "wyposazenie" },
    { bylo: "Karta „Pomieszczenia zespołu”", jest: "Sprzęt → „Potwierdź pomieszczenia…”", obszar: "wyposazenie" },
    { bylo: "Wzory w Dokumentach", jest: "Dokumenty → „Wydaj dokument…”, krok pierwszy", obszar: "dokumenty" },
    { bylo: "Procedury i instrukcje w Dokumentach", jest: "Zespół → Procedury", obszar: "procedury" },
    { bylo: "Okno grafiku", jest: "Grafik: dzień otwierany z siatki i „Ułóż grafik…”", obszar: "grafik" },
    { bylo: "Zatwierdź tydzień, Opublikuj szkic, Drukuj, CSV", jest: "Grafik → Więcej", obszar: "grafik" },
    { bylo: "Zaplanuj urlop", jest: "Urlopy → „Udziel urlopu…”", obszar: "urlopy" },
    { bylo: "Plan urlopów i wykres roczny", jest: "Urlopy → Rok", obszar: "urlopy" },
    { bylo: "Kalendarz zespołu w urlopach", jest: "Urlopy → Miesiąc", obszar: "urlopy" },
    { bylo: "Protokoły pracy zdalnej", jest: "Praca zdalna: blok „Czeka na Twoją decyzję” i dzień w siatce", obszar: "praca-zdalna" },
    { bylo: "Aktywność O365", jest: "Czas pracy → Aktywność", obszar: "czas-pracy" },
    { bylo: "Projekty działu", jest: "Praca i rozwój → Tablica", obszar: "kanban" },
    { bylo: "Dni realizacji", jest: "Zadania → Filtry → Grupuj", obszar: "todo" },
    { bylo: "Per pracownik", jest: "Filtr „Osoba” w Filtrach listy", obszar: "todo" },
    { bylo: "Notatki i ścieżka kariery", jest: "Karta osoby → Rozwój", obszar: "ocena-pracownika" },
    { bylo: "E-Learning", jest: "Praca i rozwój → Szkolenia", obszar: "e-learning" },
]);

/** Stare nazwy miejsc w tekstach serwera i powiadomień → nowe nazwy (serwera nie zmieniamy). */
export const STARE_NAZWY_MIEJSC_MOJEGO_DZIALU = Object.freeze([
    [/Mój Dział\s*(?:→|->|›|\/)\s*Pulpit/g, "Mój Dział"],
    [/Moje do wypełnienia/g, "Moje sprawy"],
    [/Aktywność O365/g, "Aktywność"],
    [/Wyposażenie IT/g, "Sprzęt"],
    [/Mój Dział\s*(?:→|->|›|\/)\s*E-Learning/g, "Mój Dział → Szkolenia"],
    [/Mój Dział\s*(?:→|->|›|\/)\s*Struktura/g, "Mój Dział → Ludzie"],
    [/Mój Dział\s*(?:→|->|›|\/)\s*Uprawnienia/g, "Mój Dział → Dostępy"],
]);

/**
 * ZWROTY ZAKAZANE W NAPISACH MODUŁU (2.15, N-7 rozszerzony) — obok wspólnych zwrotów czterech stron. Dozwolone wyjątki:
 * kod procedury („INS-ZKL-02”, „Załącznik nr 3”) w definicji pojęcia (`Pojecie`), nazwa klucza tylko na ekranie odmowy.
 */
export const ZWROTY_ZAKAZANE_MOJEGO_DZIALU = Object.freeze([
    { zwrot: "Jednostki podrz.", wzorzec: /jednostki podrz\./i, zamiast: "jednostki" },
    { zwrot: "Właściciel jednostki", wzorzec: /właściciel jednostki/i, zamiast: "kierownik jednostki" },
    { zwrot: "R / W", wzorzec: /(^|[^\p{L}])R\s*\/\s*W([^\p{L}]|$)/u, zamiast: "odczyt / odczyt i zapis" },
    { zwrot: "dyspozycj", wzorzec: /dyspozycj/i, zamiast: "plan dnia" },
    { zwrot: "fake przełożony", wzorzec: /fake przełożon/i, zamiast: "wejście imienne" },
    { zwrot: "Per pracownik", wzorzec: /per pracownik/i, zamiast: "filtr Osoba" },
    { zwrot: "Eskaluj", wzorzec: /eskaluj/i, zamiast: "priorytet Wysoki albo Pilny" },
    { zwrot: "INS-ZKL-02", wzorzec: /INS-ZKL-02/, zamiast: "ocena wdrożeniowa (kod tylko w pojęciu)", wPojeciu: true },
    { zwrot: "Załącznik nr 3", wzorzec: /załącznik nr 3/i, zamiast: "ocena wdrożeniowa (kod tylko w pojęciu)", wPojeciu: true },
    { zwrot: "DeptDocsView", wzorzec: /DeptDocsView/, zamiast: "nazwa klucza tylko na ekranie odmowy" },
    { zwrot: "DepartmentManagerAccess", wzorzec: /DepartmentManagerAccess/, zamiast: "nazwa klucza tylko na ekranie odmowy" },
    { zwrot: "zad.", wzorzec: /(^|[^\p{L}])zad\./iu, zamiast: "zadania" },
]);

/** Zwroty zakazane modułu obecne w tekście; `{ wPojeciu: true }` puszcza kody procedury (tekst definicji pojęcia). */
export const znajdzZwrotyMojegoDzialu = (tekst, { wPojeciu = false } = {}) =>
    ZWROTY_ZAKAZANE_MOJEGO_DZIALU.filter((z) => !(wPojeciu && z.wPojeciu) && z.wzorzec.test(String(tekst ?? ""))).map((z) => z.zwrot);

// ── Stare adresy (2.13, zapadka MD-10) ─────────────────────────────────────────────────────────

const kopia = (parametry) => new URLSearchParams(parametry);

/**
 * STARE ADRESY MODUŁU → `{ obszar, zapytanie }` albo `null` (adres aktualny). Czysta funkcja, wołana przez powłokę przy KAŻDEJ
 * zmianie adresu (`celStaregoAdresu`, `replace`); zapytanie przechodzi W CAŁOŚCI (także `?dzial=` — ZM-20), znikają tylko
 * parametry starego układu. Adres gołego miejsca (`/moj-dzial/wnioski` z powiadomienia) jest aktualny — otwiera żeton domyślny.
 * Nieznany segment idzie na start wspólną regułą powłoki (`dopasujObszar`), bez wiersza tutaj.
 * @param {{ reszta: string, parametry: URLSearchParams }} wejscie
 */
export const stareAdresyMojegoDzialu = ({ reszta, parametry }) => {
    const segment = String(reszta || "").split("/")[0];
    const p = parametry instanceof URLSearchParams ? parametry : new URLSearchParams(parametry || "");

    // „/moj-dzial/pulpit”, „/my-dept/overview” → start (menu, wyszukiwarka, linki w bazie przepisane przez DbInitializer).
    if (reszta === "pulpit" || reszta === "overview") return { obszar: "start", zapytanie: kopia(p) };

    // „/moj-dzial/plan-urlopow”, „/my-dept/vacation-plan” → Urlopy w skali roku.
    if (reszta === "plan-urlopow" || reszta === "vacation-plan") {
        const nowe = kopia(p);
        if (!nowe.get("widok")) nowe.set("widok", "rok");
        return { obszar: "urlopy", zapytanie: nowe };
    }

    // „/moj-dzial/wnioski?zakladka=rozliczenia” (TerminRozliczenBackgroundService) → Moje sprawy › sekcja rozliczeń.
    if ((segment === "wnioski" || segment === "requests") && reszta === segment && p.get("zakladka") === "rozliczenia") {
        const nowe = kopia(p);
        nowe.delete("zakladka");
        nowe.set("sekcja", "rozliczenia");
        return { obszar: "wnioski-przelozonego", zapytanie: nowe };
    }

    // „/moj-dzial/czas-pracy?od=&do=” bez `okres` (powrót z pełnego ekranu osoby, stare zakładki) → „Inny okres”.
    if ((reszta === "czas-pracy" || reszta === "worktime") && !p.get("okres") && (p.has("od") || p.has("do"))) {
        const nowe = new URLSearchParams();
        nowe.set("okres", "inny");
        p.forEach((wartosc, nazwa) => nowe.append(nazwa, wartosc));
        return { obszar: "czas-pracy", zapytanie: nowe };
    }

    // „/moj-dzial/procedury?doc=ID&akcja=przypisz|ludzie” (powiadomienia, Wykaz KSC) → kreator albo część karty procedury.
    if ((reszta === "procedury" || reszta === "procedures") && p.get("doc") && p.get("akcja")) {
        const akcja = p.get("akcja");
        if (akcja !== "przypisz" && akcja !== "ludzie") return null;
        const nowe = kopia(p);
        nowe.delete("akcja");
        if (akcja === "przypisz") {
            nowe.set("kreator", "przypisz-ludziom");
            nowe.set("krok", "1");
        } else {
            nowe.set("czesc", "potwierdzenia");
        }
        return { obszar: "procedury", zapytanie: nowe };
    }
    return null;
};
